<template>
  <div class="map-container">
    <div
      ref="mapElement"
      class="map"
      role="region"
      aria-label="旅行先と予定の地図"
    ></div>
    <p
      v-if="fallbackNotice"
      class="map-provider-status"
      role="status"
      aria-live="polite"
    >
      {{ fallbackNotice }}
    </p>
  </div>
</template>

<script>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { GOOGLE_MAPS_API_KEY } from "@/config/api.js";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

let googleMapsLoader = null;
let googleMapsAuthFailed = false;
let previousGoogleMapsAuthFailure = null;
let googleMapsFailureLogged = false;

const logGoogleMapsFailureOnce = () => {
  if (googleMapsFailureLogged) return;
  googleMapsFailureLogged = true;
  console.warn(
    "[MapComponent] Google Maps failed to load; displaying the Leaflet fallback.",
  );
};

const createGoogleMapsAuthError = () =>
  new Error(
    "Google Maps authentication failed. Check the API key, billing, enabled APIs, and HTTP referrer restrictions.",
  );

const hasGoogleMapsAuthFailed = () =>
  googleMapsAuthFailed || Boolean(window.__rabiitripGoogleMapsAuthFailed);

const installGoogleMapsAuthFailureHandler = (reject) => {
  if (!previousGoogleMapsAuthFailure && window.gm_authFailure) {
    previousGoogleMapsAuthFailure = window.gm_authFailure;
  }
  window.gm_authFailure = () => {
    googleMapsAuthFailed = true;
    window.__rabiitripGoogleMapsAuthFailed = true;
    if (typeof previousGoogleMapsAuthFailure === "function") {
      previousGoogleMapsAuthFailure();
    }
    reject(createGoogleMapsAuthError());
  };
};

const waitForGoogleMaps = (script) =>
  new Promise((resolve, reject) => {
    installGoogleMapsAuthFailureHandler(reject);
    if (hasGoogleMapsAuthFailed()) {
      reject(createGoogleMapsAuthError());
      return;
    }

    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      if (window.google?.maps) {
        settled = true;
        resolve(window.google.maps);
      }
    };

    script.addEventListener("load", finish);
    script.addEventListener("error", () => {
      if (!settled) {
        settled = true;
        reject(new Error("Google Maps failed to load."));
      }
    });

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.google?.maps) {
        window.clearInterval(timer);
        finish();
      } else if (Date.now() - startedAt > 10000) {
        window.clearInterval(timer);
        if (!settled) {
          settled = true;
          reject(new Error("Google Maps load timed out."));
        }
      }
    }, 100);
  });

const loadGoogleMaps = () => {
  if (hasGoogleMapsAuthFailed()) {
    return Promise.reject(createGoogleMapsAuthError());
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error("Google Maps API key is not configured."));
  }

  if (!googleMapsLoader) {
    googleMapsLoader = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[data-rabiitrip-google-maps], script[src*="maps.googleapis.com/maps/api/js"]',
      );
      if (existingScript) {
        waitForGoogleMaps(existingScript).then(resolve).catch(reject);
        return;
      }

      const script = document.createElement("script");
      const params = new URLSearchParams({
        key: GOOGLE_MAPS_API_KEY,
        libraries: "places",
        v: "weekly",
        loading: "async",
        language: "ja",
        region: "JP",
      });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.dataset.rabiitripGoogleMaps = "true";
      installGoogleMapsAuthFailureHandler(reject);
      script.onload = () => {
        if (hasGoogleMapsAuthFailed()) {
          reject(createGoogleMapsAuthError());
          return;
        }
        waitForGoogleMaps(script).then(resolve).catch(reject);
      };
      script.onerror = () => reject(new Error("Google Maps failed to load."));
      document.head.appendChild(script);
    });
  }

  return googleMapsLoader;
};

export default {
  name: "MapComponent",
  props: {
    schedules: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    titleField: {
      type: String,
      default: "SUBJECT_NMV",
    },
    timeField: {
      type: String,
      default: "START_TIME",
    },
    addressField: {
      type: String,
      default: "ADDRESS_NMV",
    },
    categoryField: {
      type: String,
      default: "CATEGORY_CDV",
    },
    center: {
      type: Array,
      default: () => [35.6762, 139.6503],
    },
    zoom: {
      type: Number,
      default: 10,
    },
    itemKeyField: {
      type: String,
      default: "",
    },
    focusedItemKey: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    const mapElement = ref(null);
    const fallbackNotice = ref("");
    let provider = "google";
    let googleMap = null;
    let leafletMap = null;
    let resizeObserver = null;
    const markers = [];
    let activeInfoWindow = null;

    const getCoordinate = (item, uppercaseKey, lowercaseKey) => {
      const value = item?.[uppercaseKey] ?? item?.[lowercaseKey];
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        return null;
      }
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };

    const getValidItems = () => {
      const sourceItems = props.items.length ? props.items : props.schedules;
      return sourceItems
        .map((item) => ({
          item,
          lat: getCoordinate(item, "LATITUDE", "latitude"),
          lng: getCoordinate(item, "LONGITUDE", "longitude"),
        }))
        .filter(
          ({ lat, lng }) =>
            lat !== null &&
            lng !== null &&
            lat >= -90 &&
            lat <= 90 &&
            lng >= -180 &&
            lng <= 180,
        );
    };

    const getCenterArray = () => [
      Number(props.center?.[0]) || 35.6762,
      Number(props.center?.[1]) || 139.6503,
    ];

    const getCenterLiteral = () => {
      const [lat, lng] = getCenterArray();
      return { lat, lng };
    };

    const getMarkerLabel = (item, index) =>
      String(item?.MAP_PIN_NUMBER ?? item?.mapPinNumber ?? index + 1);

    const getItemKey = (item) => {
      if (props.itemKeyField && item?.[props.itemKeyField] !== undefined) {
        return item[props.itemKeyField];
      }
      return item?.LINE_NON ?? item?.WISH_NO ?? item?.id ?? item?.ID ?? null;
    };

    const getGoogleMarkerIcon = () => ({
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
      fillColor: "#b4cf9e",
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 2,
      scale: 1.45,
      anchor: new window.google.maps.Point(12, 24),
      labelOrigin: new window.google.maps.Point(12, 9),
    });

    const refreshMapSize = async () => {
      await nextTick();
      requestAnimationFrame(() => {
        if (provider === "google" && googleMap) {
          window.google?.maps?.event.trigger(googleMap, "resize");
        }
        if (provider === "leaflet" && leafletMap) {
          leafletMap.invalidateSize();
        }
      });
    };

    const clearMarkers = () => {
      markers.forEach(({ marker }) => {
        if (provider === "google") {
          marker.setMap(null);
        } else {
          marker.remove();
        }
      });
      activeInfoWindow = null;
      markers.length = 0;
    };

    const focusMarker = (focusedKey = props.focusedItemKey) => {
      if (focusedKey === null || focusedKey === undefined || focusedKey === "") {
        return;
      }
      const target = markers.find(
        ({ key }) => String(key) === String(focusedKey),
      );
      if (!target) return;

      const zoomLevel = Math.max(props.zoom, 15);
      if (provider === "google" && googleMap) {
        const position = { lat: target.lat, lng: target.lng };
        googleMap.panTo(position);
        if ((googleMap.getZoom?.() || 0) < zoomLevel) {
          googleMap.setZoom(zoomLevel);
        }
        if (activeInfoWindow && activeInfoWindow !== target.infoWindow) {
          activeInfoWindow.close();
        }
        target.infoWindow.open(googleMap, target.marker);
        activeInfoWindow = target.infoWindow;
      } else if (provider === "leaflet" && leafletMap) {
        leafletMap.setView([target.lat, target.lng], zoomLevel, {
          animate: true,
        });
        target.marker.openPopup();
      }
    };

    const fitGoogleToPins = (validItems) => {
      if (!googleMap || !window.google?.maps) return;
      if (validItems.length === 0) {
        googleMap.setCenter(getCenterLiteral());
        googleMap.setZoom(props.zoom);
        return;
      }
      if (validItems.length === 1) {
        googleMap.setCenter({ lat: validItems[0].lat, lng: validItems[0].lng });
        googleMap.setZoom(Math.max(props.zoom, 15));
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      validItems.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));
      googleMap.fitBounds(bounds, 64);
    };

    const fitLeafletToPins = (validItems) => {
      if (!leafletMap) return;
      if (validItems.length === 0) {
        leafletMap.setView(getCenterArray(), props.zoom);
        return;
      }
      if (validItems.length === 1) {
        leafletMap.setView(
          [validItems[0].lat, validItems[0].lng],
          Math.max(props.zoom, 15),
        );
        return;
      }
      const bounds = L.latLngBounds(
        validItems.map(({ lat, lng }) => [lat, lng]),
      );
      leafletMap.fitBounds(bounds, { padding: [64, 64] });
    };

    const getSafeGoogleMapsUrl = (item) => {
      const url = item?.googleMapsUrl;
      if (typeof url !== "string") return null;
      try {
        const parsed = new URL(url);
        if (
          parsed.protocol !== "https:" ||
          parsed.hostname !== "www.google.com" ||
          parsed.port ||
          parsed.username ||
          parsed.password ||
          parsed.pathname !== "/maps/search/" ||
          parsed.hash
        ) {
          return null;
        }

        const allowedParams = new Set(["api", "query", "query_place_id"]);
        if ([...parsed.searchParams.keys()].some((key) => !allowedParams.has(key))) {
          return null;
        }
        const apiValues = parsed.searchParams.getAll("api");
        const queryValues = parsed.searchParams.getAll("query");
        const placeIdValues = parsed.searchParams.getAll("query_place_id");
        if (
          apiValues.length !== 1 ||
          apiValues[0] !== "1" ||
          queryValues.length !== 1 ||
          !queryValues[0].trim() ||
          placeIdValues.length > 1 ||
          (placeIdValues.length === 1 && !placeIdValues[0].trim())
        ) {
          return null;
        }
        return parsed.toString();
      } catch {
        return null;
      }
    };

    const getPopupContent = (item) => `
      <div class="map-popup">
        <h4>${escapeHtml(item[props.titleField] || "")}</h4>
        ${
          item[props.timeField]
            ? `<p><strong>時間:</strong> ${escapeHtml(item[props.timeField])}</p>`
            : ""
        }
        ${
          item[props.addressField]
            ? `<p><strong>住所:</strong> ${escapeHtml(item[props.addressField])}</p>`
            : ""
        }
        ${
          getSafeGoogleMapsUrl(item)
            ? `<a class="map-popup-google-link" href="${escapeHtml(
                getSafeGoogleMapsUrl(item),
              )}" target="_blank" rel="noopener noreferrer" aria-label="Google Mapsで開く（新しいタブ）">Google Mapsで開く</a>`
            : ""
        }
      </div>
    `;

    const updateGoogleMarkers = () => {
      clearMarkers();
      if (!googleMap || !window.google?.maps) return;

      const validItems = getValidItems();
      validItems.forEach(({ item, lat, lng }, index) => {
        const label = getMarkerLabel(item, index);
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: googleMap,
          title: item[props.titleField] || "",
          icon: getGoogleMarkerIcon(),
          label: {
            text: label,
            color: "#ffffff",
            fontSize: "13px",
            fontWeight: "700",
          },
        });
        const infoWindow = new window.google.maps.InfoWindow({
          content: getPopupContent(item),
        });
        marker.addListener("click", () => {
          if (activeInfoWindow && activeInfoWindow !== infoWindow) {
            activeInfoWindow.close();
          }
          infoWindow.open(googleMap, marker);
          activeInfoWindow = infoWindow;
        });
        markers.push({
          marker,
          infoWindow,
          item,
          key: getItemKey(item),
          lat,
          lng,
        });
      });

      fitGoogleToPins(validItems);
      focusMarker();
      refreshMapSize();
    };

    const updateLeafletMarkers = () => {
      clearMarkers();
      if (!leafletMap) return;

      const validItems = getValidItems();
      validItems.forEach(({ item, lat, lng }, index) => {
        const label = getMarkerLabel(item, index);
        const marker = L.marker([lat, lng], {
          icon: L.divIcon({
            className: "modern-map-marker",
            html: `<span>${escapeHtml(label)}</span>`,
            iconSize: [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -32],
          }),
        }).addTo(leafletMap);
        marker.bindPopup(getPopupContent(item));
        markers.push({
          marker,
          item,
          key: getItemKey(item),
          lat,
          lng,
        });
      });

      fitLeafletToPins(validItems);
      focusMarker();
      refreshMapSize();
    };

    const updateMarkers = () => {
      if (provider === "google") {
        updateGoogleMarkers();
      } else {
        updateLeafletMarkers();
      }
    };

    const initLeafletMap = () => {
      if (!mapElement.value) return;
      provider = "leaflet";
      if (mapElement.value) {
        mapElement.value.innerHTML = "";
      }
      googleMap = null;
      leafletMap = L.map(mapElement.value).setView(getCenterArray(), props.zoom);
      L.tileLayer(
        "https://tile.openstreetmap.jp/styles/osm-bright-ja/{z}/{x}/{y}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 20,
          detectRetina: true,
        },
      ).addTo(leafletMap);
      updateLeafletMarkers();
    };

    const initMap = async () => {
      if (!mapElement.value) return;
      try {
        await loadGoogleMaps();
        if (!mapElement.value) return;
        fallbackNotice.value = "";
        provider = "google";
        googleMap = new window.google.maps.Map(mapElement.value, {
          center: getCenterLiteral(),
          zoom: props.zoom,
          mapTypeId: "terrain",
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        });
        updateGoogleMarkers();
      } catch {
        logGoogleMapsFailureOnce();
        if (!mapElement.value) return;
        fallbackNotice.value =
          "Google マップを読み込めなかったため、代替地図を表示しています。";
        initLeafletMap();
      }
    };

    const escapeHtml = (value) =>
      String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    onMounted(() => {
      initMap();
      if (window.ResizeObserver && mapElement.value) {
        resizeObserver = new ResizeObserver(refreshMapSize);
        resizeObserver.observe(mapElement.value);
      }
      setTimeout(refreshMapSize, 250);
    });

    onBeforeUnmount(() => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      clearMarkers();
      if (leafletMap) {
        leafletMap.remove();
      }
      googleMap = null;
      leafletMap = null;
    });

    watch(
      () => [props.schedules, props.items, props.center, props.zoom],
      () => {
        updateMarkers();
      },
      { deep: true },
    );

    watch(
      () => props.focusedItemKey,
      () => {
        focusMarker();
      },
    );

    return {
      fallbackNotice,
      mapElement,
    };
  },
};
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.map {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.map-provider-status {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

</style>

<style>
.modern-map-marker {
  position: relative;
  width: 32px;
  height: 32px;
  background: #b4cf9e;
  border: 3px solid #ffffff;
  border-radius: 999px 999px 999px 0;
  box-shadow: 0 8px 18px rgba(180, 207, 158, 0.35);
  transform: rotate(-45deg);
}

.modern-map-marker span {
  position: absolute;
  inset: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
  border-radius: 999px;
  transform: rotate(45deg);
}

.map-popup h4 {
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.map-popup p {
  margin: 4px 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.map-popup-google-link {
  display: inline-flex;
  align-items: center;
  min-width: 44px;
  min-height: 44px;
  margin-top: 8px;
  color: #46623a;
  font-size: 0.875rem;
  font-weight: 700;
}

.map-popup-google-link:hover,
.map-popup-google-link:focus-visible {
  color: #2f4726;
  text-decoration-thickness: 2px;
}
</style>
